import React, { useEffect, useMemo, useState } from "react";
import api from "../api/axios";
import DoctorReviewForm from "../components/DoctorReviewForm";
import "./ReviewsPage.responsive.css";

function normalizeStatus(status) {
  return String(status || "draft").toLowerCase();
}

function normalizeSeverity(severity) {
  return String(severity || "untriaged").toLowerCase();
}

function ReviewStatCard({ label, value, helper, primary = false }) {
  return (
    <div className={primary ? "mgreviews-stat primary" : "mgreviews-stat"}>
      <span>{label}</span>
      <strong>{value}</strong>
      <p>{helper}</p>
    </div>
  );
}

function ReviewSignalGraph({ stats }) {
  const maxValue = Math.max(stats.total, stats.pending, stats.reviewed, 1);

  const bars = [
    {
      label: "Queue",
      value: stats.total,
      color: "linear-gradient(180deg, #18b7ff, #1f7bc1)",
    },
    {
      label: "Pending",
      value: stats.pending,
      color: "linear-gradient(180deg, #fbbf24, #f59e0b)",
    },
    {
      label: "Reviewed",
      value: stats.reviewed,
      color: "linear-gradient(180deg, #62d7ff, #2d8fe0)",
    },
  ];

  return (
    <div className="mgreviews-signal-card">
      <div className="mgreviews-signal-head">
        <div>
          <span>Review Signal</span>
          <strong>Clinical queue activity</strong>
        </div>
        <em>Live</em>
      </div>

      <div className="mgreviews-bars">
        {bars.map((bar) => {
          const height = Math.max(22, Math.round((bar.value / maxValue) * 100));

          return (
            <div className="mgreviews-bar-item" key={bar.label}>
              <div className="mgreviews-bar-track">
                <i
                  style={{
                    height: `${height}%`,
                    background: bar.color,
                  }}
                />
              </div>
              <b>{bar.value}</b>
              <small>{bar.label}</small>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ReviewWorkflowPanel({ stats, selectedSession }) {
  const pending = Math.max(stats.pending, 0);

  const workflow = [
    {
      title: "Session",
      helper: "Patient context",
      active: stats.total > 0,
    },
    {
      title: "AI Output",
      helper: "MedGen summary",
      active: stats.total > 0,
    },
    {
      title: "Reviewer",
      helper: "Doctor notes",
      active: Boolean(selectedSession),
    },
    {
      title: "Decision",
      helper: "Clinical action",
      active: selectedSession && normalizeStatus(selectedSession.status) !== "draft",
    },
    {
      title: "Case Sheet",
      helper: "Export ready",
      active: stats.reviewed > 0,
    },
  ];

  let nextAction = "Generate a clinical session first";
  if (stats.total > 0) nextAction = "Select a review item from the queue";
  if (selectedSession) {
    nextAction = `Review ${selectedSession.ownerName || "selected patient"} session`;
  }
  if (!selectedSession && pending > 0) nextAction = "Start with pending reviews";

  return (
    <div className="mgreviews-workflow-card">
      <div className="mgreviews-workflow-head">
        <div>
          <span>Reviewer Intelligence</span>
          <strong>Clinical validation pipeline</strong>
        </div>
        <em>{pending} pending</em>
      </div>

      <div className="mgreviews-flow">
        {workflow.map((item, index) => (
          <div
            key={item.title}
            className={
              item.active ? "mgreviews-flow-step active" : "mgreviews-flow-step"
            }
          >
            <b>{index + 1}</b>
            <span>{item.title}</span>
            <small>{item.helper}</small>
          </div>
        ))}
      </div>

      <div className="mgreviews-next-action">
        <span>Next Action</span>
        <strong>{nextAction}</strong>
        <p>
          Use this queue to verify AI summaries, add reviewer notes, and move
          sessions toward a clean clinical case sheet.
        </p>

        {selectedSession && (
          <div className="mgreviews-selected-mini">
            <div>
              <span>Selected Review</span>
              <strong>{selectedSession.title || `Session #${selectedSession.id}`}</strong>
              <small>
                {selectedSession.ownerName || "Patient"} ·{" "}
                {selectedSession.severity || "untriaged"} ·{" "}
                {selectedSession.status || "draft"}
              </small>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ReviewHeroPanel({ stats, selectedSession }) {
  return (
    <div className="mgreviews-hero-panel">
      <div className="mgreviews-panel-top">
        <div>
          <span>AI Status</span>
          <strong>Clinical review mode</strong>
        </div>
        <em>{selectedSession ? "Selected" : "Ready"}</em>
      </div>

      <p>
        Review records are loaded with patient context, queue state, severity
        signal, and doctor note workflow visibility.
      </p>

      <ReviewSignalGraph stats={stats} />
    </div>
  );
}

function getSeverityClass(severity) {
  const value = normalizeSeverity(severity);

  if (value === "critical" || value === "high") return "danger";
  if (value === "moderate") return "warning";
  if (value === "low") return "success";
  return "neutral";
}

function getStatusClass(status) {
  const value = normalizeStatus(status);

  if (value === "reviewed" || value === "completed") return "success";
  if (value === "draft") return "warning";
  if (value === "failed") return "danger";
  return "neutral";
}

export default function ReviewsPage({ user }) {
  const [patients, setPatients] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);

  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [severityFilter, setSeverityFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    loadReviewQueue();
  }, []);

  const loadReviewQueue = async () => {
    try {
      setLoading(true);

      const patientRes = await api.get("/api/patient/");
      const patientList = patientRes.data || [];
      setPatients(patientList);

      const sessionGroups = await Promise.all(
        patientList.map(async (patient) => {
          try {
            const sessionRes = await api.get(`/api/session/patient/${patient.id}`);

            return (sessionRes.data || []).map((session) => ({
              ...session,
              ownerName: patient.name,
              ownerRelation: patient.relation || "self",
              patientGender: patient.gender || "",
              patientAge: patient.age || "",
            }));
          } catch {
            return [];
          }
        })
      );

      const nextSessions = sessionGroups.flat();

      nextSessions.sort((a, b) => Number(b.id || 0) - Number(a.id || 0));

      setSessions(nextSessions);

      setSelectedSession((prev) => {
        if (!prev) return null;
        return nextSessions.find((s) => s.id === prev.id) || null;
      });
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not load review queue");
    } finally {
      setLoading(false);
    }
  };

  const filteredSessions = useMemo(() => {
    return sessions.filter((session) => {
      const q = search.trim().toLowerCase();

      const haystack = [
        session.title,
        session.summary,
        session.ownerName,
        session.ownerRelation,
        session.status,
        session.severity,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      const matchesSearch = !q || haystack.includes(q);

      const matchesStatus =
        statusFilter === "all" ||
        normalizeStatus(session.status) === statusFilter.toLowerCase();

      const matchesSeverity =
        severityFilter === "all" ||
        normalizeSeverity(session.severity) === severityFilter.toLowerCase();

      return matchesSearch && matchesStatus && matchesSeverity;
    });
  }, [sessions, search, statusFilter, severityFilter]);

  const visibleSessions = useMemo(() => {
    return showAll ? filteredSessions : filteredSessions.slice(0, 10);
  }, [filteredSessions, showAll]);

  const stats = useMemo(() => {
    const reviewed = sessions.filter((s) =>
      ["reviewed", "completed"].includes(normalizeStatus(s.status))
    ).length;

    return {
      total: sessions.length,
      pending: sessions.filter((s) => normalizeStatus(s.status) === "draft").length,
      reviewed,
      highPriority: sessions.filter((s) =>
        ["critical", "high"].includes(normalizeSeverity(s.severity))
      ).length,
      patients: patients.length,
    };
  }, [sessions, patients]);

  return (
    <div className="mgreviews-page">
      <style>{reviewsCss}</style>

      <section className="mgreviews-hero">
        <div className="mgreviews-hero-copy">
          <div className="mgreviews-kicker">Reviews</div>
          <h1>Clinical Review Queue</h1>
          <p>
            Review MedGen outputs, triage summaries, severity signals, and
            doctor notes from one focused clinical validation workbench.
          </p>

          <div className="mgreviews-actions">
            <button
              type="button"
              className="mgreviews-btn primary"
              onClick={loadReviewQueue}
              disabled={loading}
            >
              {loading ? "Refreshing..." : "Refresh Queue"}
            </button>

            <button
              type="button"
              className="mgreviews-btn soft"
              onClick={() => setShowAll((prev) => !prev)}
              disabled={loading}
            >
              {showAll ? "Show less" : "See all reviews"}
            </button>

            {selectedSession && (
              <button
                type="button"
                className="mgreviews-btn ghost"
                onClick={() => setSelectedSession(null)}
              >
                Clear Selection
              </button>
            )}
          </div>

          <ReviewWorkflowPanel
            stats={stats}
            selectedSession={selectedSession}
          />
        </div>

        <ReviewHeroPanel stats={stats} selectedSession={selectedSession} />
      </section>

      <section className="mgreviews-stats-grid">
        <ReviewStatCard
          primary
          label="Total queue"
          value={stats.total}
          helper="All sessions loaded into review workspace"
        />

        <ReviewStatCard
          label="Pending"
          value={stats.pending}
          helper="Sessions still awaiting clinical review"
        />

        <ReviewStatCard
          label="Reviewed"
          value={stats.reviewed}
          helper="Sessions completed by reviewer workflow"
        />

        <ReviewStatCard
          label="High priority"
          value={stats.highPriority}
          helper="Critical or high-severity sessions"
        />
      </section>

      <section className="mgreviews-filter-card">
        <div className="mgreviews-filter-grid">
          <input
            className="mgreviews-input"
            placeholder="Search reviews by patient, session, status, severity"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <select
            className="mgreviews-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All statuses</option>
            <option value="draft">Pending</option>
            <option value="completed">Completed</option>
            <option value="reviewed">Reviewed</option>
          </select>

          <select
            className="mgreviews-select"
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
          >
            <option value="all">All severity</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="moderate">Moderate</option>
            <option value="low">Low</option>
            <option value="untriaged">Untriaged</option>
          </select>

          <button
            type="button"
            className="mgreviews-btn soft"
            onClick={() => {
              setSearch("");
              setStatusFilter("all");
              setSeverityFilter("all");
            }}
          >
            Reset Filters
          </button>
        </div>
      </section>

      <section className="mgreviews-layout">
        <div className="mgreviews-queue-card">
          <div className="mgreviews-card-head">
            <div>
              <span>Queue</span>
              <strong>Review Items</strong>
              <p>Pick a session to inspect and annotate.</p>
            </div>

            <em>{filteredSessions.length} shown</em>
          </div>

          {loading ? (
            <div className="mgreviews-empty">Loading review queue...</div>
          ) : filteredSessions.length === 0 ? (
            <div className="mgreviews-empty">
              <h3>No review items found</h3>
              <p>Adjust your filters or generate more sessions.</p>
            </div>
          ) : (
            <div className="mgreviews-queue-list">
              {visibleSessions.map((session) => (
                <button
                  key={session.id}
                  type="button"
                  className={
                    selectedSession?.id === session.id
                      ? "mgreviews-queue-item active"
                      : "mgreviews-queue-item"
                  }
                  onClick={() => setSelectedSession(session)}
                >
                  <div className="mgreviews-queue-top">
                    <div>
                      <span>Clinical Record</span>
                      <strong>{session.title || `Session #${session.id}`}</strong>
                    </div>

                    <div className="mgreviews-pill-stack">
                      <em
                        className={`mgreviews-pill ${getSeverityClass(
                          session.severity
                        )}`}
                      >
                        {session.severity || "untriaged"}
                      </em>
                      <em
                        className={`mgreviews-pill ${getStatusClass(
                          session.status
                        )}`}
                      >
                        {session.status || "draft"}
                      </em>
                    </div>
                  </div>

                  <p>
                    {session.ownerName || "Patient"} ·{" "}
                    {session.ownerRelation || "self"} ·{" "}
                    {session.patientAge ? `${session.patientAge} yrs` : "age —"}
                  </p>

                  {session.summary && (
                    <small className="mgreviews-summary">
                      {session.summary}
                    </small>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="mgreviews-detail-card">
          {!selectedSession ? (
            <div className="mgreviews-detail-empty">
              <div className="mgreviews-review-orb">
                <i />
                <span />
                <b />
              </div>

              <h3>No session selected</h3>
              <p>
                Select a queue item to open reviewer notes and doctor form
                controls. The selected review will appear here.
              </p>
            </div>
          ) : (
            <>
              <div className="mgreviews-detail-head">
                <div>
                  <span>Detail</span>
                  <strong>
                    {selectedSession.title || `Session #${selectedSession.id}`}
                  </strong>
                  <p>
                    {selectedSession.ownerName} · severity:{" "}
                    {selectedSession.severity || "untriaged"} · status:{" "}
                    {selectedSession.status || "draft"}
                  </p>
                </div>

                <button
                  type="button"
                  className="mgreviews-btn ghost"
                  onClick={() => setSelectedSession(null)}
                >
                  Clear
                </button>
              </div>

              <div className="mgreviews-doctor-form-wrap">
                <DoctorReviewForm
                  sessionId={selectedSession.id}
                  user={user}
                  analysis={selectedSession.analysis || null}
                />
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}

const reviewsCss = `
.mgreviews-page {
  --mg-black: #03060c;
  --mg-navy: #06101f;
  --mg-navy-2: #081525;
  --mg-blue: #1f7bc1;
  --mg-cyan: #18b7ff;
  --mg-text: #07111f;
  --mg-muted: #66778e;
  --mg-border: #cddcec;
  --mg-soft: #f6faff;

  width: 100%;
  min-height: calc(100vh - 68px);
  padding: 28px 34px 46px;
  color: var(--mg-text);
}

.mgreviews-page * {
  box-sizing: border-box;
}

.mgreviews-hero {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 430px;
  gap: 26px;
  align-items: stretch;
  margin-bottom: 22px;
  padding: 30px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid var(--mg-border);
  box-shadow: 0 20px 55px rgba(7, 17, 31, 0.07);
}

.mgreviews-kicker {
  margin-bottom: 12px;
  color: #125a94;
  font-size: 11px;
  font-weight: 950;
  letter-spacing: 0.22em;
  text-transform: uppercase;
}

.mgreviews-hero h1 {
  margin: 0;
  color: var(--mg-text);
  font-size: clamp(38px, 4vw, 58px);
  line-height: 1.02;
  letter-spacing: -0.045em;
  font-weight: 950;
}

.mgreviews-hero p {
  max-width: 780px;
  margin: 18px 0 18px;
  color: var(--mg-muted);
  font-size: 15px;
  line-height: 1.7;
}

.mgreviews-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 22px;
}

.mgreviews-btn {
  min-height: 40px;
  padding: 10px 16px;
  border-radius: 11px;
  border: 1px solid transparent;
  cursor: pointer;
  font-size: 13px;
  font-weight: 900;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: 0.16s ease;
  white-space: nowrap;
}

.mgreviews-btn:disabled {
  opacity: 0.52;
  cursor: not-allowed;
}

.mgreviews-btn.primary {
  color: white;
  background: linear-gradient(135deg, #1f7bc1, #0f5f9d);
  box-shadow: 0 14px 28px rgba(31, 123, 193, 0.22);
}

.mgreviews-btn.soft,
.mgreviews-btn.ghost {
  color: #112238;
  background: #f8fbff;
  border-color: var(--mg-border);
}

.mgreviews-hero-panel {
  padding: 22px;
  border-radius: 22px;
  color: white;
  background:
    radial-gradient(circle at 92% 0%, rgba(24, 183, 255, 0.20), transparent 34%),
    linear-gradient(145deg, #03060c, #081525 62%, #06101f);
  border: 1px solid rgba(24,183,255,0.18);
  box-shadow: 0 20px 55px rgba(3, 6, 12, 0.16);
}

.mgreviews-panel-top,
.mgreviews-signal-head,
.mgreviews-workflow-head,
.mgreviews-card-head,
.mgreviews-detail-head {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: flex-start;
}

.mgreviews-panel-top span,
.mgreviews-signal-head span,
.mgreviews-workflow-head span,
.mgreviews-card-head span,
.mgreviews-detail-head span {
  display: block;
  margin-bottom: 8px;
  color: #9fdcff;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.mgreviews-panel-top strong {
  color: white;
  font-size: 22px;
  letter-spacing: -0.04em;
}

.mgreviews-panel-top em,
.mgreviews-workflow-head em,
.mgreviews-card-head em {
  padding: 5px 10px;
  border-radius: 999px;
  font-style: normal;
  color: #bfeaff;
  background: rgba(24,183,255,0.12);
  border: 1px solid rgba(24,183,255,0.18);
  font-size: 11px;
  font-weight: 900;
}

.mgreviews-hero-panel p {
  color: #a9bdd3;
  margin: 18px 0;
}

/* Hero signal */

.mgreviews-signal-card {
  padding: 16px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.075);
  border: 1px solid rgba(24,183,255,0.13);
}

.mgreviews-signal-head strong {
  color: white;
  font-size: 16px;
}

.mgreviews-signal-head em {
  color: #bfeaff;
  font-style: normal;
  font-size: 11px;
  font-weight: 900;
}

.mgreviews-bars {
  height: 145px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  align-items: end;
  margin-top: 16px;
}

.mgreviews-bar-item {
  display: grid;
  justify-items: center;
  gap: 7px;
}

.mgreviews-bar-track {
  width: 58px;
  height: 88px;
  display: flex;
  align-items: end;
  padding: 5px;
  border-radius: 16px;
  background: rgba(255,255,255,0.10);
  border: 1px solid rgba(255,255,255,0.10);
}

.mgreviews-bar-track i {
  width: 100%;
  min-height: 18px;
  border-radius: 12px;
  animation: mgreviewsBarRise 0.75s ease both;
}

.mgreviews-bar-item b {
  color: white;
  font-size: 18px;
  font-weight: 950;
}

.mgreviews-bar-item small {
  color: #a9bdd3;
  font-size: 11px;
  font-weight: 850;
}

/* Workflow */

.mgreviews-workflow-card {
  margin-top: 10px;
  padding: 20px;
  border-radius: 22px;
  background:
    radial-gradient(circle at 96% 0%, rgba(24, 183, 255, 0.13), transparent 34%),
    linear-gradient(180deg, #ffffff 0%, #f7fbff 100%);
  border: 1px solid var(--mg-border);
  box-shadow: 0 14px 38px rgba(7, 17, 31, 0.055);
}

.mgreviews-workflow-head span {
  color: #125a94;
}

.mgreviews-workflow-head strong {
  display: block;
  color: var(--mg-text);
  font-size: 22px;
  font-weight: 950;
  letter-spacing: -0.045em;
}

.mgreviews-workflow-head em {
  color: #0b4e84;
  background: rgba(31, 123, 193, 0.10);
  border-color: rgba(31, 123, 193, 0.18);
}

.mgreviews-flow {
  position: relative;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
  margin: 18px 0 20px;
}

.mgreviews-flow::before {
  content: "";
  position: absolute;
  left: 8%;
  right: 8%;
  top: 21px;
  height: 3px;
  border-radius: 999px;
  background:
    linear-gradient(
      90deg,
      rgba(31,123,193,0.12),
      rgba(24,183,255,0.42),
      rgba(31,123,193,0.12)
    );
  background-size: 220% 100%;
  animation: mgreviewsFlow 2.8s linear infinite;
}

.mgreviews-flow::after {
  content: "";
  position: absolute;
  top: 17px;
  left: 8%;
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: #18b7ff;
  box-shadow:
    0 0 0 6px rgba(24,183,255,0.12),
    0 0 22px rgba(24,183,255,0.55);
  animation: mgreviewsFlowDot 4.5s ease-in-out infinite;
}

.mgreviews-flow-step {
  position: relative;
  z-index: 1;
  display: grid;
  justify-items: center;
  text-align: center;
  gap: 5px;
}

.mgreviews-flow-step b {
  width: 44px;
  height: 44px;
  display: grid;
  place-items: center;
  border-radius: 15px;
  color: #0b4e84;
  background: #eef6ff;
  border: 1px solid var(--mg-border);
  font-size: 13px;
  font-weight: 950;
}

.mgreviews-flow-step.active b {
  color: white;
  background:
    radial-gradient(circle at 30% 20%, rgba(255,255,255,0.28), transparent 28%),
    linear-gradient(135deg, #1f7bc1, #0f5f9d);
  border-color: transparent;
  animation: mgreviewsNodeBreath 2.2s ease-in-out infinite;
}

.mgreviews-flow-step span {
  color: var(--mg-text);
  font-size: 12px;
  font-weight: 950;
}

.mgreviews-flow-step small {
  color: var(--mg-muted);
  font-size: 10px;
  font-weight: 750;
}

.mgreviews-next-action {
  padding: 18px;
  border-radius: 18px;
  background:
    radial-gradient(circle at 100% 0%, rgba(31, 123, 193, 0.10), transparent 34%),
    #ffffff;
  border: 1px solid #d9e8f6;
}

.mgreviews-next-action span,
.mgreviews-selected-mini span {
  display: block;
  margin-bottom: 8px;
  color: #125a94;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}

.mgreviews-next-action strong {
  display: block;
  color: var(--mg-text);
  font-size: 18px;
  font-weight: 950;
  letter-spacing: -0.035em;
}

.mgreviews-next-action p {
  margin: 10px 0 0;
  color: var(--mg-muted);
  font-size: 13px;
  line-height: 1.55;
}

.mgreviews-selected-mini {
  margin-top: 14px;
  padding: 13px;
  border-radius: 14px;
  background: #eef6ff;
  border: 1px solid #d9e8f6;
}

.mgreviews-selected-mini small {
  display: block;
  margin-top: 4px;
  color: var(--mg-muted);
  font-weight: 800;
}

/* Stats */

.mgreviews-stats-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 18px;
}

.mgreviews-stat {
  min-height: 128px;
  padding: 20px;
  border-radius: 18px;
  border: 1px solid var(--mg-border);
  background: rgba(255,255,255,0.94);
  box-shadow: 0 12px 35px rgba(7, 17, 31, 0.055);
}

.mgreviews-stat.primary {
  color: white;
  background: linear-gradient(135deg, #1f7bc1, #125a94);
  border-color: transparent;
}

.mgreviews-stat span {
  display: block;
  margin-bottom: 10px;
  color: #75869d;
  font-size: 11px;
  font-weight: 950;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.mgreviews-stat.primary span {
  color: rgba(255,255,255,0.75);
}

.mgreviews-stat strong {
  display: block;
  color: var(--mg-text);
  font-size: 28px;
  font-weight: 950;
}

.mgreviews-stat.primary strong {
  color: white;
}

.mgreviews-stat p {
  margin: 8px 0 0;
  color: var(--mg-muted);
  line-height: 1.5;
}

.mgreviews-stat.primary p {
  color: rgba(255,255,255,0.86);
}

/* Filters */

.mgreviews-filter-card {
  margin-bottom: 18px;
}

.mgreviews-filter-grid {
  display: grid;
  grid-template-columns: minmax(260px, 1fr) 170px 170px auto;
  gap: 10px;
}

.mgreviews-input,
.mgreviews-select {
  width: 100%;
  min-height: 42px;
  padding: 10px 13px;
  border-radius: 12px;
  border: 1px solid var(--mg-border);
  background: rgba(255,255,255,0.96);
  color: #07111f;
  font-size: 13px;
  font-weight: 750;
  outline: none;
}

.mgreviews-input:focus,
.mgreviews-select:focus {
  border-color: #1f7bc1;
  box-shadow: 0 0 0 4px rgba(31, 123, 193, 0.12);
}

/* Layout */

.mgreviews-layout {
  display: grid;
  grid-template-columns: minmax(360px, 0.82fr) minmax(0, 1.18fr);
  gap: 18px;
  align-items: start;
}

.mgreviews-queue-card,
.mgreviews-detail-card {
  padding: 20px;
  border-radius: 22px;
  border: 1px solid var(--mg-border);
  background: rgba(255,255,255,0.96);
  box-shadow: 0 14px 38px rgba(7,17,31,0.06);
}

.mgreviews-card-head {
  margin-bottom: 16px;
}

.mgreviews-card-head span,
.mgreviews-detail-head span {
  color: #125a94;
}

.mgreviews-card-head strong,
.mgreviews-detail-head strong {
  display: block;
  color: var(--mg-text);
  font-size: 24px;
  letter-spacing: -0.045em;
  font-weight: 950;
}

.mgreviews-card-head p,
.mgreviews-detail-head p {
  margin: 7px 0 0;
  color: var(--mg-muted);
  line-height: 1.5;
}

.mgreviews-card-head em {
  color: #0b4e84;
  background: rgba(31, 123, 193, 0.10);
  border-color: rgba(31, 123, 193, 0.18);
}

.mgreviews-queue-list {
  display: grid;
  gap: 12px;
  max-height: 780px;
  overflow: auto;
  padding-right: 4px;
}

.mgreviews-queue-item {
  width: 100%;
  text-align: left;
  padding: 16px;
  border-radius: 18px;
  border: 1px solid var(--mg-border);
  background:
    radial-gradient(circle at 100% 0%, rgba(31, 123, 193, 0.08), transparent 34%),
    #ffffff;
  cursor: pointer;
  transition: 0.16s ease;
}

.mgreviews-queue-item:hover {
  transform: translateY(-2px);
  border-color: rgba(31, 123, 193, 0.55);
  box-shadow:
    0 0 0 1px rgba(24, 183, 255, 0.14),
    0 18px 44px rgba(31, 123, 193, 0.12);
}

.mgreviews-queue-item.active {
  border-color: rgba(24,183,255,0.72);
  background: #eef6ff;
  box-shadow:
    0 0 0 2px rgba(24,183,255,0.18),
    0 18px 44px rgba(31,123,193,0.14);
}

.mgreviews-queue-top {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: flex-start;
}

.mgreviews-queue-top span {
  display: block;
  margin-bottom: 7px;
  color: #125a94;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}

.mgreviews-queue-top strong {
  display: block;
  color: #07111f;
  font-size: 17px;
  letter-spacing: -0.035em;
  font-weight: 950;
}

.mgreviews-queue-item p {
  margin: 12px 0 0;
  color: var(--mg-muted);
  font-size: 13px;
  line-height: 1.45;
}

.mgreviews-summary {
  display: -webkit-box;
  margin-top: 11px;
  color: #66778e;
  font-size: 12px;
  line-height: 1.45;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.mgreviews-pill-stack {
  display: grid;
  gap: 7px;
  justify-items: end;
}

.mgreviews-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 25px;
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 900;
  font-style: normal;
  text-transform: capitalize;
}

.mgreviews-pill.neutral {
  color: #0b4e84;
  background: rgba(31, 123, 193, 0.10);
  border: 1px solid rgba(31, 123, 193, 0.18);
}

.mgreviews-pill.warning {
  color: #92400e;
  background: rgba(245, 158, 11, 0.12);
  border: 1px solid rgba(245, 158, 11, 0.24);
}

.mgreviews-pill.danger {
  color: #991b1b;
  background: rgba(239, 68, 68, 0.10);
  border: 1px solid rgba(239, 68, 68, 0.22);
}

.mgreviews-pill.success {
  color: #166534;
  background: rgba(34, 197, 94, 0.11);
  border: 1px solid rgba(34, 197, 94, 0.22);
}

.mgreviews-empty,
.mgreviews-detail-empty {
  padding: 24px;
  border-radius: 18px;
  background: #f7fbff;
  border: 1px solid #d9e8f6;
  color: var(--mg-text);
}

.mgreviews-empty h3,
.mgreviews-detail-empty h3 {
  margin: 0 0 8px;
  color: var(--mg-text);
}

.mgreviews-empty p,
.mgreviews-detail-empty p {
  color: var(--mg-muted);
  line-height: 1.55;
}

.mgreviews-detail-empty {
  min-height: 480px;
  display: grid;
  place-items: center;
  text-align: center;
  align-content: center;
}

.mgreviews-review-orb {
  position: relative;
  width: 118px;
  height: 118px;
  margin-bottom: 18px;
  border-radius: 50%;
  background:
    radial-gradient(circle at 32% 24%, rgba(255,255,255,0.32), transparent 24%),
    linear-gradient(145deg, #18b7ff, #1f7bc1);
  box-shadow:
    0 0 0 12px rgba(24,183,255,0.08),
    0 24px 65px rgba(24,183,255,0.22);
  animation: mgreviewsOrbPulse 2.4s ease-in-out infinite;
}

.mgreviews-review-orb i,
.mgreviews-review-orb span,
.mgreviews-review-orb b {
  position: absolute;
  left: 50%;
  top: 50%;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  border: 1px solid rgba(255,255,255,0.34);
}

.mgreviews-review-orb i {
  width: 82px;
  height: 82px;
}

.mgreviews-review-orb span {
  width: 50px;
  height: 50px;
}

.mgreviews-review-orb b {
  width: 16px;
  height: 16px;
  background: white;
  border: none;
  box-shadow: 0 0 22px rgba(255,255,255,0.7);
}

.mgreviews-detail-head {
  margin-bottom: 18px;
}

.mgreviews-doctor-form-wrap {
  min-width: 0;
}

.mgreviews-page .card,
.mgreviews-page .review-card,
.mgreviews-page .form-card,
.mgreviews-page textarea,
.mgreviews-page input,
.mgreviews-page select {
  color: var(--mg-text);
}

.mgreviews-page textarea,
.mgreviews-page input,
.mgreviews-page select {
  border-color: var(--mg-border);
}

@keyframes mgreviewsBarRise {
  from {
    transform: scaleY(0.4);
    opacity: 0.45;
    transform-origin: bottom;
  }

  to {
    transform: scaleY(1);
    opacity: 1;
    transform-origin: bottom;
  }
}

@keyframes mgreviewsFlow {
  0% {
    background-position: 220% 0;
  }

  100% {
    background-position: -220% 0;
  }
}

@keyframes mgreviewsFlowDot {
  0% {
    left: 8%;
    opacity: 0;
    transform: scale(0.82);
  }

  8% {
    opacity: 1;
    transform: scale(1);
  }

  50% {
    left: 50%;
  }

  92% {
    opacity: 1;
  }

  100% {
    left: 88%;
    opacity: 0;
    transform: scale(0.82);
  }
}

@keyframes mgreviewsNodeBreath {
  0% {
    box-shadow:
      0 14px 28px rgba(31, 123, 193, 0.22),
      0 0 0 0 rgba(24, 183, 255, 0.18);
  }

  50% {
    box-shadow:
      0 18px 36px rgba(31, 123, 193, 0.28),
      0 0 0 7px rgba(24, 183, 255, 0.06);
  }

  100% {
    box-shadow:
      0 14px 28px rgba(31, 123, 193, 0.22),
      0 0 0 0 rgba(24, 183, 255, 0.18);
  }
}

@keyframes mgreviewsOrbPulse {
  0%, 100% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.05);
  }
}

@media (max-width: 1280px) {
  .mgreviews-hero {
    grid-template-columns: 1fr;
  }

  .mgreviews-hero-panel {
    max-width: 620px;
  }

  .mgreviews-stats-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .mgreviews-layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 1100px) {
  .mgreviews-page {
    padding: 20px;
  }

  .mgreviews-filter-grid {
    grid-template-columns: 1fr 1fr;
  }

  .mgreviews-flow {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .mgreviews-flow::before,
  .mgreviews-flow::after {
    display: none;
  }

  .mgreviews-flow-step {
    grid-template-columns: 44px 1fr;
    justify-items: start;
    text-align: left;
    padding: 12px;
    border-radius: 16px;
    background: rgba(255,255,255,0.75);
    border: 1px solid #d9e8f6;
  }
}

@media (max-width: 760px) {
  .mgreviews-page {
    padding: 14px;
  }

  .mgreviews-hero {
    padding: 20px;
  }

  .mgreviews-hero h1 {
    font-size: 36px;
  }

  .mgreviews-actions {
    flex-direction: column;
  }

  .mgreviews-btn {
    width: 100%;
  }

  .mgreviews-stats-grid {
    grid-template-columns: 1fr;
  }

  .mgreviews-filter-grid {
    grid-template-columns: 1fr;
  }

  .mgreviews-queue-top,
  .mgreviews-detail-head {
    flex-direction: column;
  }

  .mgreviews-pill-stack {
    display: flex;
    flex-wrap: wrap;
    justify-items: start;
  }
}
`;